import React, { useMemo } from 'react';
import { FilterCheckableItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterCheckableItem';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { FilterSingleItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterSingleItem';
import { FilterSelectAllItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterSelectAllItem';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { TypographyTextColorVariant } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';

export interface FilterListBoxItem {
  /**
   * The item translated content
   */
  translatedContent: TranslatedContent;

  /**
   * The item value (should be unique)
   */
  value: string;

  /**
   * The selected state of the item
   */
  selected: boolean;

  icon?: React.FC<SvgProps> | null;

  color?: TypographyTextColorVariant;
}

export interface FilterListBoxProps {
  /**
   * Whether you want to allow multiple selections or not
   */
  multiple: boolean;

  /**
   * The items in the list box
   */
  items: FilterListBoxItem[];

  /**
   * Callback when items are selected or deselected
   * @param newValue
   */
  onUpdate: (params: {
    addedSelections: string[];
    removedSelections: string[];
  }) => void;
}

export const FilterListBox = ({
  multiple,
  items,
  onUpdate,
}: FilterListBoxProps) => {
  const selectedValues = useMemo(() => {
    const values = items
      .filter((item) => item.selected)
      .map((item) => item.value);

    if (multiple) {
      return values;
    }

    if (values.length === 0) {
      return [];
    }

    const firstSelected = values[0];
    return [firstSelected];
  }, [items, multiple]);

  return (
    <Spacing direction={'vertical'} size={'none'}>
      {multiple && (
        <FilterSelectAllItem
          onClick={(action) => {
            if (action === 'add') {
              onUpdate({
                addedSelections: items.map((item) => item.value),
                removedSelections: [],
              });
            }

            if (action === 'remove') {
              onUpdate({
                addedSelections: [],
                removedSelections: items.map((item) => item.value),
              });
            }
          }}
          checked={items.every((item) => item.selected)}
        />
      )}
      {items.map((item) => (
        <div key={item.value}>
          {!multiple && (
            <FilterSingleItem
              onClick={() => {
                onUpdate({
                  addedSelections: [item.value],
                  removedSelections: selectedValues,
                });
              }}
              selected={item.selected}
              translatedContent={item.translatedContent}
              icon={item.icon ?? null}
              color={item.color ?? null}
              itemValue={item.value}
            />
          )}

          {multiple && (
            <FilterCheckableItem
              onChange={(newChecked) => {
                if (newChecked) {
                  onUpdate({
                    addedSelections: [item.value],
                    removedSelections: [],
                  });
                } else {
                  onUpdate({
                    addedSelections: [],
                    removedSelections: [item.value],
                  });
                }
              }}
              checked={item.selected}
              itemValue={item.value}
              translatedContent={item.translatedContent}
            />
          )}
        </div>
      ))}
    </Spacing>
  );
};
